import { Animator, Arrow, ArrowType, BaseClass, EdgeStyleBase, GeneralPath, IAnimation, IArrow, ICanvasContext, IEdge, Insets, IRenderContext, Point, PolylineEdgeStyle, Rect, SvgVisual, SvgVisualGroup, TimeSpan, Visual } from '@yfiles/yfiles';
import { MODEL_COLOURS } from '../../model-services/model-item-enums';
import { EDGE_TYPE } from '../../common/linqset-model';

export const svgns = 'http://www.w3.org/2000/svg';

const MIN_THICKNESS = 2
const MAX_THICKNESS = 9

// how wide an edge is drawn: in proportion to its value when the canvas is showing values, otherwise the style's width.
// Drawing, clicking and visibility all use this, so a thick edge can be clicked across its whole width
const edgeThickness = (graph:any, edge:IEdge, baseThickness:number) => {
    const max = graph?.tag?.maxEdgeValue
    const thickness = max ? 10 * (Number(edge.tag?.value) || 0) / max : baseThickness
    return Math.min(MAX_THICKNESS, Math.max(MIN_THICKNESS, thickness || MIN_THICKNESS))
}

export class EdgeState {
    path: GeneralPath;
    arrow?: IArrow;
    color?: string;
    thickness: number;
    animation: boolean
    dashed: boolean

    constructor(path: GeneralPath) {
        this.path = path
    }

    equals(other:any) {
        return this.pathEquals(other) && this.stateEquals(other)
    }

    stateEquals(other:any) {
        return (
          other.color === this.color && other.animation === this.animation && other.thickness === this.thickness && other.dashed === this.dashed
        )
    }

    pathEquals(other:any) {
        return other.path.hasSameValue(this.path)
    }

}

export class EdgeStyle extends EdgeStyleBase {
    useHovered:boolean = false
    pathThickness:number;
    baseArrow:Arrow
    hoverArrow:Arrow
    excludedArrow:Arrow

    constructor(private baseColour:string, private hoverColour:string, private arrowType:ArrowType) {
        super();
        this.pathThickness = 2;
        this.baseArrow = new Arrow(this.arrowType, baseColour, baseColour)
        this.hoverArrow = new Arrow(this.arrowType, hoverColour, hoverColour)
        this.excludedArrow = new Arrow(this.arrowType, MODEL_COLOURS.EXCLUDED_COLOR, MODEL_COLOURS.EXCLUDED_HOVER_COLOR)
    }

    /**
     * Creates the visual for an edge.
     * @see Overrides {@link yfiles.styles.EdgeStyleBase#createVisual}
     * @return {yfiles.view.Visual}
     */
    override createVisual(context: IRenderContext, edge: IEdge): Visual {
        // This implementation creates a CanvasContainer and uses it for the rendering of the edge.
        const g = window.document.createElementNS(svgns, 'g')
        // Get the necessary data for rendering of the edge
        const state = this.createEdgeState(context, edge)

        // Render the edge
        this.render(context, edge, g, state)

        return new SvgVisual(g)
    }

    /**
     * Re-renders the edge using the old visual for performance reasons.
     * @see Overrides {@link yfiles.styles.EdgeStyleBase#updateVisual}
     * @return {yfiles.view.Visual}
     */
    override updateVisual(context: any, oldVisual: SvgVisualGroup, edge: IEdge): Visual {
        const container = oldVisual.svgElement as Element
        // get the data with which the oldvisual was created
        const cachedState = container['data-renderDataCache']
        // get the data for the new visual
        const newState = this.createEdgeState(context, edge)

        // check if something changed
        if (!newState.stateEquals(cachedState)) {
            // more than only the path changed - re-render the visual
            while (container.firstChild) {
                container.removeChild(container.firstChild)
            }

            this.render(context, edge, container, newState)
            return oldVisual
        }

        if (!newState.pathEquals(cachedState)) {
            // only the path changed - update the old visual
            this.updatePath(context, edge, container, newState)
        }

        return oldVisual
    }

    /**
     * Creates an object containing all necessary data to create an edge visual.
     * @return {object}
     */
    createEdgeState(context:any, edge:IEdge):EdgeState {
        const state = new EdgeState(this.getPath(edge))

        const type = edge?.tag?.edge?.type
        state.dashed = type === EDGE_TYPE.USED_IN || type === EDGE_TYPE.USEBY

        if (this.useHovered) {
            state.color = this.hoverColour;
            state.arrow = this.hoverArrow;
        } else if (edge.tag && edge.tag.edge && edge.tag.edge.excluded) {
            state.color = MODEL_COLOURS.EXCLUDED_COLOR;
            state.arrow = this.excludedArrow;
        } else {
            state.color = this.baseColour;
            state.arrow = this.baseArrow;
        }

        state.animation = (edge.sourceNode?.tag?.temporalNode)?true:false
        state.thickness = edgeThickness(context.canvasComponent?.graph, edge, this.pathThickness)

        if (edge.tag?.highlight) {
            state.color = '#e011a4'
            state.arrow = new Arrow(this.arrowType, '#e011a4', '#e011a4')
            //state.thickness = 4
        }

        return state
    }

    /**
     * Creates the visual appearance of an edge.
     */
    render(context:IRenderContext, edge:IEdge, container:any, state:EdgeState) {
        const g = container

        // store information with the visual on how we created it
        g['data-renderDataCache'] = state

        const path = state.path.createSvgPath()

        path.setAttribute('fill', 'none')
        path.setAttribute('stroke-width', state.thickness.toString())
        path.setAttribute('stroke-linejoin', 'round')
        path.setAttribute('stroke', state.color)

        if (state.dashed) {
            path.setAttribute("stroke-dasharray", "5, 3");
        }

        container.appendChild(path)

        // add the arrows to the container
        super.addArrows(context, container, edge, state.path, IArrow.NONE, state.arrow)

        // edges from a node in the time simulation carry a packet that shows items moving along them
        if (edge.sourceNode?.tag?.temporalNode && edge.tag?.edge) {
            if (!container.querySelector(".packet")) {
                edge.tag.packetAnimator = new PacketAnimation(this.createPacketElement(container), edge)
            }
        }

        return container
    }

    /**
     * Updates the edge path data as well as the arrow positions of the visuals stored in <param name="container" />.
     * @param context {yfiles.view.IRenderContext}
     * @param edge {yfiles.graph.IEdge}
     * @param container {SVGElement}
     * @param cache {RenderDataCache}
     */
    updatePath(context:IRenderContext, edge:IEdge, container:any, state:EdgeState) {
        // The first child must be a path - else re-create the container from scratch
        if (container.childNodes.length === 0 || !(container.childNodes[0] instanceof SVGPathElement)) {
            while (container.firstChild) {
                container.removeChild(container.firstChild)
            }

            this.render(context, edge, container, state)
                return
        }

        // store information with the visual on how we created it
        container['data-renderDataCache'] = state

        const gp = state.path
        const path = container.childNodes[0]

        const updatedPath = gp.createSvgPath()
        path.setAttribute('d', updatedPath.getAttribute('d') || '')

        // update the arrows
        if (state.arrow) super.updateArrows(context, container, edge, gp, IArrow.NONE, state.arrow)
    }

    /**
     * Creates a {@link yfiles.geometry.GeneralPath} from the edge's bends.
     * @param {yfiles.graph.IEdge} edge The edge to create the path for.
     * @return {yfiles.geometry.GeneralPath} A {@link yfiles.geometry.GeneralPath} following the edge
     * @see Overrides {@link yfiles.styles.EdgeStyleBase#getPath}
     */
    override getPath(edge:IEdge):GeneralPath {
        // Create a general path from the locations of the ports and the bends of the edge.
        const path = new GeneralPath()
        if (!edge.sourcePort || !edge.targetPort)
            return path

        edge.sourcePort.location && path.moveTo(edge.sourcePort.location)
        edge.bends.forEach && edge.bends.forEach(bend => {
            path.lineTo(bend.location)
        })
        edge.targetPort.location && path.lineTo(edge.targetPort.location)

        // shorten the path in order to provide room for drawing the arrows.
        return super.cropPath(edge, IArrow.NONE, this.baseArrow, path)
    }

    /**
     * Determines whether the visual representation of the edge has been hit at the given location.
     * Overridden method to include the {@link MySimpleEdgeStyle#pathThickness} and the HitTestRadius specified in the
     * context in the calculation.
     * @see Overrides {@link yfiles.styles.EdgeStyleBase#isHit}
     * @return {boolean}
     */
    override isHit(canvasContext:ICanvasContext, p:Point, edge:IEdge):boolean {
        // Use the convenience method in GeneralPath
        return this.getPath(edge).pathContains(
            p,
            canvasContext.hitTestRadius + edgeThickness((canvasContext as any).canvasComponent?.graph, edge, this.pathThickness) * 0.5
        )
    }

    /**
     * Determines whether the edge is visible in the given rectangle.
     * Overridden method to improve performance of the suprt implementation
     * @see Overrides {@link yfiles.styles.EdgeStyleBase#isVisible}
     * @return {boolean}
     */
    override isVisible(context:ICanvasContext, rectangle:Rect, edge:IEdge) {
        // enlarge the test rectangle to include the path thickness
        const enlargedRectangle = rectangle.getEnlarged(new Insets(edgeThickness((context as any).canvasComponent?.graph, edge, this.pathThickness)))
        // delegate to the efficient implementation of PolylineEdgeStyle
        return helperEdgeStyle.renderer
            .getVisibilityTestable(edge, helperEdgeStyle)
            .isVisible(context, enlargedRectangle)
    }

    /**
     * Creates a packet visualization.
     * @return {SVGElement}
     */
    createPacketElement(g) {
        return createPacketElement(g, this.baseColour)
    }
}

// a packet, hidden until a PacketAnimation moves it along an edge, added to the container g
export const createPacketElement = (g:Element, colour:string) => {
    const packet = window.document.createElementNS(svgns, 'ellipse')
    packet.setAttribute('rx', '5')
    packet.setAttribute('ry', '5')
    packet.setAttribute('cx', '0')
    packet.setAttribute('cy', '0')
    packet.setAttribute('stroke', colour)
    packet.setAttribute('style', `fill: ${colour};`)
    packet.classList.add('packet')
    packet.style.setProperty('visibility', 'hidden', null)
    g.appendChild(packet)

    return packet
}

const helperEdgeStyle = new PolylineEdgeStyle({
    sourceArrow: IArrow.NONE,
    targetArrow: IArrow.NONE
})

  /**
   * An animation that moves a packet visualization along the edge path.
   * @implements {yfiles.view.IAnimation}
   */
export class PacketAnimation extends BaseClass(IAnimation) {
    locations:Point[]
    distances:number[]
    proportions:number[]
    size = 5
    running:boolean = false

    /**
     * Constructor that takes the packet Element, the Edge to move on
     * @param {Element} packet
     * @param {yfiles.graph.IEdge} edge
     */
    constructor(private packet:any, private edge:IEdge) {
        super()
    }

    /**
     * Gets the preferred duration of the animation.
     * @see Specified by {@link yfiles.view.IAnimation#preferredDuration}.
     * @type {yfiles.lang.TimeSpan}
     */
    get preferredDuration() {
        return new TimeSpan(500)
    }

    sendPacket(animator:Animator) {
        if (this.running) return
        animator.animate(this)
    }

    /**
     * Initializes the packet loction and starts the animation.
     * @see Specified by {@link yfiles.view.IAnimation#initialize}.
     */
    initialize() {
        const s = this.size

        this.running = true
        this.packet['data-animation-running'] = true
        this.packet.setAttribute('rx', s)
        this.packet.setAttribute('ry', s)

        // find the source and target point for the animation
        const path = this.edge.style.renderer.getPathGeometry(this.edge, this.edge.style).getPath()
        const pathCursor = path.createCursor()
        pathCursor.moveNext()
        this.locations = [pathCursor.currentEndPoint]
        this.distances = [0]
        this.proportions = []

        let totalDistance = 0

        while (pathCursor.moveNext()) {
            this.locations.push(pathCursor.currentEndPoint)
            const length = this.locations.length
            const distance = this.locations[length-2].distanceTo(this.locations[length-1])
            totalDistance += distance
            this.distances.push(distance)
        }

        let currentTotal = 0
        this.distances.forEach(d => {
            currentTotal += d/totalDistance
            this.proportions.push(currentTotal)
        })

        let startingPosition = this.locations[0]

        // translate to the source point
        this.packet.setAttribute(
            'transform',
            `translate(${startingPosition.x} ${startingPosition.y})`
        )

        // show the element
        this.packet.style.setProperty('visibility', 'visible', null)
    }

    /**
     * One update step. Updates the current packet location.
     * @param {number} time
     * @see Specified by {@link yfiles.view.IAnimation#animate}.
     */
    animate(time:number) {
        if (!this.proportions || this.packet.ownerDocument === null || this.packet.parentNode === null || time === 0) {
            return
        }

        const currentLocation = this.getAnimatedPosition(time)
        if (currentLocation) this.packet.setAttribute('transform', `translate(${currentLocation.x} ${currentLocation.y})`)
    }

    getAnimatedPosition(time:number) {
        for (let i=0; i < this.proportions.length; i++) {
            if (this.proportions[i] >= time) {
                const proportionOfSection = (time - this.proportions[i-1])/(this.proportions[i]-this.proportions[i-1])
                return this.locations[i-1].add(this.locations[i].subtract(this.locations[i-1]).multiply(proportionOfSection))
            }
        }
        return undefined
    }

    /**
     * Hides the packet Element.
     * @see Specified by {@link yfiles.view.IAnimation#cleanup}.
     */
    cleanUp() {
        // hide the packet element
        this.packet.style.setProperty('visibility', 'hidden', null)
        //this.gauge.style.setProperty('visibility', 'hidden', null)
        this.packet['data-animation-running'] = false
        this.running = false
    }
}


